import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '@mainstage/core-plugin-api';
import {
  WbBadge,
  TableCellProps,
  isoDateToDisplayString,
  useEntitiesByRefs,
} from '@agilelab/plugin-wb-platform';
import {
  FeatureRequestListItem,
  FeatureRequestStatus,
  FeatureRequestPriority,
  FeatureRequestView,
  ReceivedFeatureRequestFilters,
  RequesterFeatureRequestFilters,
  transformUrnToWitboostId,
  getEntityDisplayName,
} from '@agilelab/plugin-wb-builder-common';
import {
  FeatureRequestPanel,
  FeatureRequestDetail,
  FeatureRequestStatusBadge,
} from '@agilelab/plugin-wb-builder-catalog';
import { useFeatureRequestsByRequester } from './useFeatureRequestsByRequester';
import { useFeatureRequestsReceived } from './useFeatureRequestsReceived';
import { useFeatureRequestDetail } from './useFeatureRequestDetail';
import { useFeatureRequestProjectsByRequester } from './useFeatureRequestProjectsByRequester';
import { useFeatureRequestProjectsReceived } from './useFeatureRequestProjectsReceived';
import {
  MyFeatureRequestsFilters,
  MyFeatureRequestsFilterValues,
} from './MyFeatureRequestsFilters';
import { featureRequestRequesterApiRef } from '../../apis';

const PRIORITY_COLOR_MAP: Record<
  FeatureRequestPriority,
  'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'
> = {
  [FeatureRequestPriority.LOW]: 'info',
  [FeatureRequestPriority.MEDIUM]: 'warning',
  [FeatureRequestPriority.HIGH]: 'error',
  [FeatureRequestPriority.CRITICAL]: 'error',
};

export const MyFeatureRequestsPanel = () => {
  const requesterApi = useApi(featureRequestRequesterApiRef);
  const [searchParams, setSearchParams] = useSearchParams();

  const view =
    (searchParams.get('view') as FeatureRequestView) ?? FeatureRequestView.MY;
  const isReceivedView = view === FeatureRequestView.RECEIVED;

  const myFilters: RequesterFeatureRequestFilters = useMemo(
    () => ({
      status: (searchParams.get('status') as FeatureRequestStatus) || undefined,
      priority:
        (searchParams.get('priority') as FeatureRequestPriority) || undefined,
      projectUrn: searchParams.get('projectUrn') || undefined,
      offset: Number(searchParams.get('offset') || '0'),
      limit: Number(searchParams.get('limit') || '20'),
    }),
    [searchParams],
  );

  const receivedFilters: ReceivedFeatureRequestFilters = useMemo(
    () => ({
      status: (searchParams.get('status') as FeatureRequestStatus) || undefined,
      priority:
        (searchParams.get('priority') as FeatureRequestPriority) || undefined,
      projectUrn: searchParams.get('projectUrn') || undefined,
      offset: Number(searchParams.get('offset') || '0'),
      limit: Number(searchParams.get('limit') || '20'),
    }),
    [searchParams],
  );

  const {
    data: myData,
    isLoading: myIsLoading,
    error: myError,
  } = useFeatureRequestsByRequester(myFilters);

  const {
    data: receivedData,
    isLoading: receivedIsLoading,
    error: receivedError,
  } = useFeatureRequestsReceived(receivedFilters);

  const data = isReceivedView ? receivedData : myData;
  const isLoading = isReceivedView ? receivedIsLoading : myIsLoading;
  const error = isReceivedView ? receivedError : myError;
  const activeFilters = isReceivedView ? receivedFilters : myFilters;

  const detailId = searchParams.get('detail');
  const {
    data: detailData,
    isLoading: detailIsLoading,
    error: detailError,
  } = useFeatureRequestDetail(detailId ?? '');

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, val]) => {
            if (val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          });
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (filterValues: MyFeatureRequestsFilterValues) => {
      updateParams({
        view: filterValues.view ?? FeatureRequestView.MY,
        status: filterValues.status,
        priority: filterValues.priority,
        projectUrn: filterValues.projectUrn,
        offset: '0',
        detail: undefined,
      });
    },
    [updateParams],
  );

  const handleRowClick = useCallback(
    (row: FeatureRequestListItem) => {
      updateParams({ detail: row.id });
    },
    [updateParams],
  );

  const handleBackToList = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('detail');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { data: projectsByRequester } = useFeatureRequestProjectsByRequester();
  const { data: projectsReceived } = useFeatureRequestProjectsReceived();

  const availableProducts = useMemo(() => {
    const projects = isReceivedView ? projectsReceived : projectsByRequester;
    return projects ?? [];
  }, [isReceivedView, projectsByRequester, projectsReceived]);

  const urnToEntityRefPairs = useMemo(() => {
    const urnsSet = new Set<string>();
    // All product URNs across all pages (for filter dropdown + Product column)
    availableProducts.forEach(urn => urnsSet.add(urn));
    // Current page requesterRef URNs (for Requester column)
    data?.items?.forEach(item => {
      if (item.requesterRef) urnsSet.add(item.requesterRef);
    });
    return Array.from(urnsSet)
      .map(urn => ({ urn, entityRef: transformUrnToWitboostId(urn) }))
      .filter(
        (pair): pair is { urn: string; entityRef: string } => !!pair.entityRef,
      );
  }, [availableProducts, data]);

  const entityRefs = useMemo(
    () => urnToEntityRefPairs.map(p => p.entityRef),
    [urnToEntityRefPairs],
  );

  const { data: entitiesData } = useEntitiesByRefs(entityRefs, [
    'kind',
    'metadata.name',
    'metadata.namespace',
    'spec.mesh.name',
    'spec.mesh.version',
  ]);

  const urnDisplayNames = useMemo((): Record<string, string> => {
    if (!entitiesData?.items) return {};
    const map: Record<string, string> = {};
    entitiesData.items.forEach((entity, idx) => {
      if (entity) {
        const urn = urnToEntityRefPairs[idx]?.urn;
        if (urn) map[urn] = getEntityDisplayName(entity);
      }
    });
    return map;
  }, [entitiesData, urnToEntityRefPairs]);

  const baseColumns: TableCellProps<FeatureRequestListItem>[] = useMemo(
    () => [
      {
        headerName: 'Summary',
        field: 'summary',
        minWidth: 300,
        tooltip: true,
      },
      {
        headerName: 'Status',
        field: 'status',
        fieldRender: (row: FeatureRequestListItem) => (
          <FeatureRequestStatusBadge status={row.status} />
        ),
      },
      {
        headerName: 'Priority',
        field: 'priority',
        fieldRender: (row: FeatureRequestListItem) => (
          <WbBadge color={PRIORITY_COLOR_MAP[row.priority]} variant="soft">
            {row.priority}
          </WbBadge>
        ),
      },
      {
        headerName: 'Product',
        field: 'projectUrn',
        fieldRender: (row: FeatureRequestListItem) =>
          row.projectUrn
            ? urnDisplayNames[row.projectUrn] ?? row.projectUrn
            : '—',
      },
      {
        headerName: 'Date',
        field: 'createdAt',
        fieldRender: (row: FeatureRequestListItem) =>
          isoDateToDisplayString(row.createdAt, {
            showHour: false,
            showMinute: false,
            showSecond: false,
          }),
        typographyProps: { style: { whiteSpace: 'nowrap' } },
      },
    ],
    [urnDisplayNames],
  );

  const receivedColumns: TableCellProps<FeatureRequestListItem>[] = useMemo(
    () => [
      ...baseColumns,
      {
        headerName: 'Requester',
        field: 'requesterRef',
        fieldRender: (row: FeatureRequestListItem) =>
          row.requesterRef
            ? urnDisplayNames[row.requesterRef] ?? row.requesterRef
            : '—',
      },
    ],
    [baseColumns, urnDisplayNames],
  );

  const columns = isReceivedView ? receivedColumns : baseColumns;

  return (
    <FeatureRequestPanel
      data={data}
      isLoading={isLoading}
      error={error ? (error as Error) : null}
      showActions={isReceivedView}
      columns={columns}
      noDataLabel={
        isReceivedView
          ? 'No feature requests received for your systems.'
          : 'No feature requests found.'
      }
      onRowClick={handleRowClick}
      filterComponent={
        <MyFeatureRequestsFilters
          values={{
            view,
            status: activeFilters.status,
            priority: activeFilters.priority,
            projectUrn: activeFilters.projectUrn,
          }}
          onChange={handleFilterChange}
          availableProducts={availableProducts}
          urnDisplayNames={urnDisplayNames}
        />
      }
      detailComponent={
        <FeatureRequestDetail
          featureRequestId={detailId}
          open={!!detailId}
          onClose={handleBackToList}
          readOnly={!isReceivedView}
          featureRequest={detailData}
          isLoading={detailIsLoading}
          error={detailError ? (detailError as Error) : null}
          fetchAttachmentBlob={(frId, attachmentId) =>
            requesterApi.downloadAttachment(frId, attachmentId)
          }
        />
      }
      pagination={{
        count: data?.total ?? 0,
        limit: activeFilters.limit,
        currentPage: Math.floor(activeFilters.offset / activeFilters.limit),
        rowsPerPageOptions: [20, 50, 100],
        onPageChange: (page: number) =>
          updateParams({ offset: String(page * activeFilters.limit) }),
        onRowsPerPageChange: (rowsPerPage: number) =>
          updateParams({ limit: String(rowsPerPage), offset: '0' }),
      }}
    />
  );
};
